import { annualBreakdown } from './annualBreakdown';

/**
 * A CUSTOM WINDOW over the ledger: the same figures, for a stretch the reader names.
 *
 * <h2>It computes NOTHING, and that is the entire design</h2>
 *
 * The arithmetic is {@link annualBreakdown}'s, which is itself pinned against the backend's
 * `LedgerStatistics` loop for loop and carries a TOTAL row computed by that same code. So a window
 * summary is a FILTER followed by that total — win rate, profit factor, compounded return, dollars
 * and drawdown, all from one implementation.
 *
 * <p>Writing a second accumulator here would have been the defect this repository keeps recording:
 * the Running Ledger summed percentages while the Simulator compounded them, and the two reported
 * different Net PnL for the same trades with profit factor agreeing by accident. A window panel
 * disagreeing with the annual table directly above it would be that again, in the worst possible
 * place — two numbers about the same trades, a centimetre apart.
 *
 * <h2>DATE PICKERS, not the chart's own dataZoom window</h2>
 *
 * The brief allowed either. The zoom window is the wrong anchor HERE specifically, and not on
 * principle: since Phase 7B the replay ADOPTS the operator's window and then dispatches a
 * threshold pan against it as the playhead advances, so a summary bound to the zoom would
 * recompute itself every few bars during playback. A figure that moves while nobody touches it is
 * not a summary of anything — and "what did it do between these two dates" is the question a
 * reader actually has, which a pair of dates answers exactly and a viewport answers approximately.
 *
 * <h2>A trade belongs to the window by its EXIT</h2>
 *
 * The brief says "opened/closed in that window" and those differ for any trade spanning an edge.
 * The exit is the basis, because that is when a trade becomes a RESULT: profit factor and the
 * drawdown curve are both built from realised outcomes, so a trade whose entry is inside the
 * window but whose exit is three months later has not contributed anything to it yet. It is also
 * the basis the annual table uses, so the years and a window covering one year agree — which they
 * would not if the two picked different ends of a trade.
 */

/** The date part of an ISO timestamp, by STRING SLICE. */
const dayOf = (iso) => {
  /*
   * Sliced, never parsed through `Date`. Exit times are offset date-times in the exchange's own
   * offset, so `new Date(iso)` resolves them in the READER's timezone and moves a
   * `2020-01-01T09:30-05:00` exit onto 2019-12-31 for anyone east of UTC. The same rule
   * `annualBreakdown.yearOf` follows, and for the same reason: two readers must not see different
   * tables from one run.
   */
  if (typeof iso !== 'string' || iso.length < 10) return null;
  const day = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
};

/** A `yyyy-mm-dd` bound, or null for "unbounded on this side". */
const boundOf = (value) => {
  if (typeof value !== 'string') return null;
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
};

/**
 * The trades whose exit falls inside `[from, to]`, both INCLUSIVE.
 *
 * <p>Inclusive on both ends because the inputs are days, not instants: a reader typing a start and
 * an end means those two days to be in it, and an exclusive end would silently drop every trade
 * that closed on the day they named. Lexicographic comparison is exact for `yyyy-mm-dd`.
 *
 * <p>Either bound may be null, which is unbounded on that side — so a reader who fills in only a
 * start gets everything from there on rather than nothing.
 *
 * @param trades stamped (`{frameIndex, trade}`) or bare; open positions are excluded, as they are
 *               from every realised figure in this codebase
 */
export function tradesInWindow(trades, { from = null, to = null } = {}) {
  const list = Array.isArray(trades) ? trades : [];
  const lo = boundOf(from);
  const hi = boundOf(to);
  return list.filter((entry) => {
    const t = entry && entry.trade ? entry.trade : entry;
    if (!t || t.open) return false;
    const day = dayOf(t.exitTime);
    if (day === null) return false;
    if (lo !== null && day < lo) return false;
    if (hi !== null && day > hi) return false;
    return true;
  });
}

/**
 * The window's figures: trade count, win rate, profit factor, compounded return, dollars and
 * drawdown — or null when the window holds no trades.
 *
 * <h2>Null rather than a row of zeros</h2>
 *
 * A window with nothing in it has no win rate and no profit factor; rendering 0% and 0.00 would
 * be two figures that read as measurements of a flat period rather than as the absence of one.
 * The caller says "no closed trades in this window", which is the honest sentence.
 *
 * <h2>The curve RESTARTS inside the window</h2>
 *
 * The drawdown is measured from the window's own peak and the return compounds from 1.0, exactly
 * as each year's row does. Carrying the run's curve in would make a window after a bad stretch
 * report the inherited drawdown — describing a period the reader did not ask about.
 *
 * @param trades the trades already bounded by the playhead; this function does not know about one
 */
export function windowSummary(trades, window) {
  const inside = tradesInWindow(trades, window);
  if (inside.length === 0) return null;
  // ONE CALL, and no arithmetic of its own: `total` is annualBreakdown's own all-trades row,
  // computed by the code the annual table and the backend are both pinned against.
  return annualBreakdown(inside).total;
}

export default windowSummary;
